import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import styled from 'astroturf/react'
const colorBackgroundInverseStandard = 'var(--colorBackgroundInverseStandard, var(--coreColorNeutral900))'
const colorBackgroundStandard = 'var(--colorBackgroundStandard, var(--coreColorNeutral0))'
const colorStrokeInverseMedium = 'var(--colorStrokeInverseMedium, var(--coreColorNeutral600))'
const colorTextInverseStandard = 'var(--colorTextInverseStandard, var(--coreColorNeutral0))'
const fontWeightStandard = 'var(--fontWeightStandard, 400)'
const fontSizeMedium = 'var(--fontSizeMedium, 0.875rem)'
const fontWeightBold = 'var(--fontWeightBold, 700)'
const fontSizeSmall = 'var(--fontSizeSmall, 0.75rem)'

import { useI18n } from 'locales'
import _TooltipContainer from "invitee_vote_tooltip-TooltipContainer.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/invitee_vote_tooltip.js?styleId=TooltipContainer";
import _TooltipBackground from "invitee_vote_tooltip-TooltipBackground.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/invitee_vote_tooltip.js?styleId=TooltipBackground";
import _List from "invitee_vote_tooltip-List.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/invitee_vote_tooltip.js?styleId=List";
import _ListItem from "invitee_vote_tooltip-ListItem.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/invitee_vote_tooltip.js?styleId=ListItem";
import _VoteCount from "invitee_vote_tooltip-VoteCount.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/invitee_vote_tooltip.js?styleId=VoteCount";


const OFFSET = 50
const TRIGGER_DELAY = 500

const TooltipContainer = /*#__PURE__*/styled('div', null, {
  displayName: "TooltipContainer",
  styles: _TooltipContainer
})

const TooltipBackground = /*#__PURE__*/styled('div', null, {
  displayName: "TooltipBackground",
  styles: _TooltipBackground
})

const List = /*#__PURE__*/styled('ul', null, {
  displayName: "List",
  styles: _List
})

const ListItem = /*#__PURE__*/styled('li', null, {
  displayName: "ListItem",
  styles: _ListItem
})

const VoteCount = /*#__PURE__*/styled('div', null, {
  displayName: "VoteCount",
  styles: _VoteCount
})

const findScrollableParent = (target) => {
  for (let currentNode = target.parentNode; target.name !== 'body'; currentNode = currentNode.parentNode) {
    if (window.getComputedStyle(currentNode).overflow !== 'visible') return currentNode
  }
  return null
}

export const useInviteeVoteTooltip = ({ invitees, anchorTop = false }) => {
  const { i18n } = useI18n()
  const timeoutRef = useRef()
  const [isTooltipShown, setTooltipShown] = useState(false)
  const [hasBottomConflict, setHasBottomConflict] = useState(false)
  const [hasRightConflict, setHasRightConflict] = useState(false)
  const tooltipRef = useRef()
  const isPhone = useDeviceType('isPhone')

  useLayoutEffect(() => {
    const tooltip = tooltipRef.current
    if (!tooltip) return

    const spotPickerContainer = findScrollableParent(tooltip)
    if (!spotPickerContainer) return

    const { bottom, right } = tooltip.getBoundingClientRect()
    const { bottom: spotPickerBottom, right: spotPickerRight } = spotPickerContainer.getBoundingClientRect()

    if (!hasRightConflict) setHasRightConflict(Math.round(right) > spotPickerRight)
    if (!hasBottomConflict) setHasBottomConflict(Math.round(bottom) > (spotPickerBottom - OFFSET))
  }, [isTooltipShown, tooltipRef, hasRightConflict, hasBottomConflict])

  const showTooltip = useCallback(() => {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      setTooltipShown(true)
    }, TRIGGER_DELAY)
  }, [])

  const hideTooltip = useCallback(() => {
    clearTimeout(timeoutRef.current)
    setTooltipShown(false)
  }, [])

  useEffect(() => () => {
    clearTimeout(timeoutRef.current)
  }, [])

  useEffect(() => {
    if (invitees.length === 0) hideTooltip()
  }, [hideTooltip, invitees])

  const TooltipComponent = useCallback(() => {
    if (!isTooltipShown || isPhone) return null

    return (
      <TooltipContainer
        role="tooltip"
        anchorTop={anchorTop}
        hasBottomConflict={hasBottomConflict}
        hasRightConflict={hasRightConflict}
        onClick={(e) => e.stopPropagation()}
        data-testid="invitee-vote-tooltip"
      >
        <TooltipBackground ref={tooltipRef}>
          <VoteCount>{i18n.t('booking.templates.poll.votes', { count: invitees.length })}</VoteCount>
          <List>
            {invitees.map((invitee) => (
              <ListItem key={invitee.id}>
                {invitee.full_name}
              </ListItem>
            ))}
          </List>
        </TooltipBackground>
      </TooltipContainer>

    )
  }, [isTooltipShown, isPhone, anchorTop, hasBottomConflict, hasRightConflict, i18n, invitees])

  const hostProps = {
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
    onClick: hideTooltip,
    'aria-label': i18n.t('booking.templates.poll.votes_tooltip_label')
  }

  return [
    TooltipComponent, hostProps
  ]
}
